import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import type { ArticleData } from '@/mocks/articles';

function renderContent(content: ArticleData['sections'][0]['content'][0]) {
  switch (content.type) {
    case 'paragraph':
      return (
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          {content.text}
        </p>
      );
    case 'list':
      return (
        <ul className="space-y-2 my-4">
          {content.items?.map((item, i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm text-foreground-700">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-checkbox-circle-line text-secondary-500 text-sm"></i>
              </div>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    case 'table':
      return (
        <div className="overflow-x-auto my-6">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-background-100 border-b border-background-200/70">
                <th className="text-left px-4 py-3 font-semibold text-foreground-800 text-xs md:text-sm whitespace-nowrap">Pozycja</th>
                <th className="text-left px-4 py-3 font-semibold text-foreground-800 text-xs md:text-sm">Opis</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200/50">
              {content.rows?.map((row, i) => (
                <tr key={i} className="hover:bg-background-100/50 transition-colors">
                  <td className="px-4 py-3 font-medium text-foreground-800 text-xs md:text-sm whitespace-nowrap">{row.name}</td>
                  <td className="px-4 py-3 text-foreground-600 text-xs md:text-sm">{row.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'tip':
      return (
        <div className="space-y-3 my-6">
          {content.tipItems?.map((tip, i) => (
            <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-secondary-50/50 border border-secondary-200/50">
              <div className="w-7 h-7 flex items-center justify-center flex-shrink-0 mt-0.5 rounded-lg bg-secondary-100">
                <i className={`${tip.icon} text-secondary-500 text-sm`}></i>
              </div>
              <div>
                <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-0.5">{tip.title}</h4>
                <p className="text-xs text-foreground-600 leading-relaxed">{tip.desc}</p>
              </div>
            </div>
          ))}
        </div>
      );
    case 'highlight':
      return (
        <div className="p-4 md:p-6 rounded-xl bg-primary-50 border border-primary-200/60 my-6">
          <h3 className="font-heading text-base font-semibold text-primary-800 mb-2">
            {content.highlightTitle}
          </h3>
          <ul className="space-y-2">
            {content.highlightItems?.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-foreground-700">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-check-line text-secondary-500 text-sm"></i>
                </div>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      );
    default:
      return null;
  }
}

export default function ArticleLayout({ article }: { article: ArticleData }) {
  const siteUrl = import.meta.env.VITE_SITE_URL;
  const articleUrl = `${siteUrl}/artykul/${article.slug}`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    image: article.heroImage,
    url: articleUrl,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
    author: { '@type': 'Organization', name: 'Kakobuy Polska' },
    datePublished: '2026-07-22',
    dateModified: '2026-07-22',
    inLanguage: 'pl',
    publisher: {
      '@type': 'Organization',
      name: 'Kakobuy Polska',
      url: siteUrl,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Strona główna',
        item: `${siteUrl}/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.breadcrumbLabel,
        item: articleUrl,
      },
    ],
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <div className="min-h-screen bg-background-50">
        <header>
          <Navbar />
        </header>

        <main className="pt-20">
          <article className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8 flex-wrap" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                Strona główna
              </Link>
              <i className="ri-arrow-right-s-line text-xxs"></i>
              <span className="text-foreground-400 whitespace-nowrap">{article.breadcrumbLabel}</span>
            </nav>

            <header className="mb-10">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="px-3 py-1 rounded-md bg-accent-100 text-accent-700 text-xs font-semibold whitespace-nowrap">
                  {article.category}
                </span>
                <span className="text-xs text-foreground-500">{article.readTime}</span>
                <span className="text-xs text-foreground-500">·</span>
                <span className="text-xs text-foreground-500">{article.date}</span>
              </div>

              <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-5 leading-tight">
                {article.title}
              </h1>

              <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-6">
                {article.description}
              </p>

              <div className="rounded-2xl overflow-hidden h-[250px] md:h-[380px] bg-background-200 mb-8">
                <img
                  src={article.heroImage}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                  title={article.title}
                />
              </div>
            </header>

            <div className="prose-custom">
              {article.sections.map((section) => (
                <section key={section.id} className="mb-10">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                    {section.heading}
                  </h2>
                  {section.content.map((block, i) => (
                    <div key={i}>{renderContent(block)}</div>
                  ))}
                </section>
              ))}

              <section className="mb-6">
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  Wróć na{' '}
                  <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium cursor-pointer">
                    stronę główną
                  </Link>
                  , aby przeglądać wszystkie kategorie Kakobuy Spreadsheet i rozpocząć zakupy!
                </p>
              </section>
            </div>

            {/* CTA */}
            <div className="mt-10 p-6 md:p-8 rounded-2xl bg-gradient-to-r from-primary-50 to-accent-50 border border-primary-200/40 text-center">
              <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-2">
                {article.ctaTitle}
              </h3>
              <p className="text-sm text-foreground-600 mb-5 max-w-md mx-auto leading-relaxed">
                {article.ctaSubtitle}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  Kakobuy Spreadsheet
                  <i className="ri-arrow-right-line text-sm"></i>
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-background-50 border border-background-300/60 text-foreground-800 text-sm font-semibold hover:bg-background-100 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  Wróć na stronę główną
                  <i className="ri-home-line text-sm"></i>
                </Link>
              </div>
            </div>

            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}